import { utc } from "@date-fns/utc"
import { CaretRight } from "@phosphor-icons/react"
import { addDays } from "date-fns"
import { useState } from "react"

import { Button, StatRow } from "~/components/design-system/components"
import { Tooltip } from "~/components/reusable/Tooltip"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { formatNumber, pluralize } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import type { RouterOutputs } from "~/utils/api"
import { maxSkuCount, summarizeTimeline, type TimelineWeek } from "./timelineSummary"

type StoreTimeline = RouterOutputs["allocation"]["getStoreTimeline"]

const utcDay = (date: string) => new Date(`${date}T00:00:00Z`)

const weekLabel = (weekStart: string) => {
  const start = utcDay(weekStart)
  const end = addDays(start, 6, { in: utc })
  return `${formatDate(start, DateFormatType.Week)} · ${formatDate(start, DateFormatType.FullDateShort)} to ${formatDate(end, DateFormatType.FullDateShort)}`
}

const weekCounts = (week: TimelineWeek) =>
  `${pluralize(week.skuCount, "SKU")} and ${pluralize(week.variantCount, "variant")} active`

/** A month's label sits on its first week; the year shows on January's and on the first week. */
function monthLabel(weeks: TimelineWeek[], index: number): string | null {
  const start = utcDay(weeks[index]!.weekStart)
  if (index > 0 && start.getUTCDate() > 7) return null
  if (index === 0 || start.getUTCMonth() === 0) return formatDate(start, DateFormatType.MonthYear)
  return formatDate(start, DateFormatType.Month)
}

function shadeClass(count: number, max: number): string {
  if (count === 0) return "bg-component"
  const share = count / max
  if (share > 0.75) return "bg-interactive"
  if (share > 0.5) return "bg-interactive opacity-75"
  if (share > 0.25) return "bg-interactive opacity-50"
  return "bg-interactive opacity-25"
}

function WeekBars({
  weeks,
  currentWeekStart,
}: {
  weeks: TimelineWeek[]
  currentWeekStart: string
}) {
  const max = maxSkuCount(weeks)
  return (
    <div className="flex flex-col gap-1">
      <div className="flex h-16 items-end gap-px" role="list" aria-label="SKUs active per week">
        {weeks.map((week) => {
          const current = week.weekStart === currentWeekStart
          return (
            <Tooltip
              key={week.weekStart}
              title={weekLabel(week.weekStart)}
              description={current ? `This week. ${weekCounts(week)}` : weekCounts(week)}
            >
              <div
                role="listitem"
                aria-label={`${weekLabel(week.weekStart)}: ${weekCounts(week)}`}
                className={cn(
                  "flex h-full min-w-0 flex-1 items-end rounded-sm",
                  current && "bg-highlight",
                )}
              >
                <div
                  className={cn(
                    "w-full rounded-t-sm",
                    week.skuCount === 0 ? "h-0.5 bg-component" : "bg-interactive",
                    week.skuCount > 0 && !current && "opacity-60",
                  )}
                  style={
                    week.skuCount > 0
                      ? { height: `${Math.max(6, (week.skuCount / max) * 100)}%` }
                      : undefined
                  }
                />
              </div>
            </Tooltip>
          )
        })}
      </div>
      <div className="flex gap-px">
        {weeks.map((week, index) => (
          <div key={week.weekStart} className="relative h-4 min-w-0 flex-1">
            <span className="absolute left-0 whitespace-nowrap paragraph-xs text-fg-muted">
              {monthLabel(weeks, index)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function PackageRows({ packages, currentWeekStart }: StoreTimeline) {
  return (
    <div className="flex flex-col gap-1.5">
      {packages.map((pkg) => {
        const max = maxSkuCount(pkg.weeks)
        return (
          <div key={pkg.id} className="flex flex-col gap-0.5">
            <span className="truncate paragraph-xs text-fg-subtle" title={pkg.name}>
              {pkg.name}
            </span>
            <div className="flex h-3 gap-px">
              {pkg.weeks.map((week) => (
                <Tooltip
                  key={week.weekStart}
                  title={`${pkg.name} · ${weekLabel(week.weekStart)}`}
                  description={weekCounts(week)}
                >
                  <div
                    className={cn(
                      "min-w-0 flex-1 rounded-[2px]",
                      shadeClass(week.skuCount, max),
                      week.weekStart === currentWeekStart && "shadow-borders-focus",
                    )}
                  />
                </Tooltip>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** One bar per week, this week tinted, and optionally one strip per package. */
export function StoreCoverageTimeline({ timeline }: { timeline: StoreTimeline }) {
  const [perPackage, setPerPackage] = useState(false)
  const summary = summarizeTimeline(timeline.weeks, timeline.currentWeekStart)
  const weekOf = (week: TimelineWeek | null) =>
    week ? formatDate(utcDay(week.weekStart), DateFormatType.Week) : "None"

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <StatRow
          label="Variants this week"
          value={formatNumber(summary.current?.variantCount ?? 0)}
        />
        <StatRow label="SKUs this week" value={formatNumber(summary.current?.skuCount ?? 0)} />
        <StatRow label="Typical week (SKUs)" value={formatNumber(summary.typicalSkuCount)} />
        <StatRow
          label="Peak"
          value={
            summary.peak
              ? `${formatNumber(summary.peak.skuCount)} SKUs · ${weekOf(summary.peak)}`
              : "None"
          }
        />
        <StatRow
          label="Lowest week ahead"
          value={
            summary.lowestAhead
              ? `${formatNumber(summary.lowestAhead.skuCount)} SKUs · ${weekOf(summary.lowestAhead)}`
              : "None"
          }
        />
      </div>
      <WeekBars weeks={timeline.weeks} currentWeekStart={timeline.currentWeekStart} />
      {timeline.packages.length > 0 && (
        <>
          <Button
            variant="transparent"
            size="xs"
            className="self-start"
            aria-expanded={perPackage}
            onClick={() => setPerPackage((open) => !open)}
          >
            <CaretRight className={cn("transition-transform", perPackage && "rotate-90")} />
            {perPackage ? "Hide" : "Show"} timeline per package ({timeline.packages.length})
          </Button>
          {perPackage && <PackageRows {...timeline} />}
        </>
      )}
    </div>
  )
}
